/**
 * TVITVIS STANDARDER SOM DATA (G4, QA13 punkt 70; migrering 0942).
 *
 * Ett sted, `app.platform_defaults`, som Control Room redigerer: scenetidene, standard personfelt og
 * ressursmalene («Start fra et eksempel»). Nye årganger og nye organisasjoner leser lista; en årgang
 * eller organisasjon som alt finnes, røres aldri av en endring her (253.44).
 *
 * `/v1/platform/defaults` er bare for en plattformadmin (404 for andre, som resten av Control Room).
 * `/v1/resource-type-presets` er malene som tilbys i typebyggeren, for alle som kan lage en type.
 */
import { z } from 'zod';

import { PERSON_FIELD_SURFACES } from './participations.js';
import { createResourceTypeRequestSchema } from './resources.js';

export const PLATFORM_DEFAULT_KINDS = ['stage_time', 'person_field', 'resource_type'] as const;
export type PlatformDefaultKind = (typeof PLATFORM_DEFAULT_KINDS)[number];

/** Det A-tid la på scenetiden (0924): binding, «opptar scenen», standardvarighet og strek. */
export const stageTimeDefaultSettingsSchema = z
  .object({
    binding: z.enum(['row', 'performance_start', 'performance_end']),
    occupiesStage: z.boolean(),
    defaultMinutes: z.number().int().min(1).max(1440).nullable(),
    stageLine: z.boolean(),
  })
  .meta({ id: 'StageTimeDefaultSettings' });
export type StageTimeDefaultSettings = z.infer<typeof stageTimeDefaultSettingsSchema>;

export const personFieldDefaultSettingsSchema = z
  .object({
    type: z.string(),
    options: z.array(z.string()),
    shownIn: z.array(z.enum(PERSON_FIELD_SURFACES)),
  })
  .meta({ id: 'PersonFieldDefaultSettings' });
export type PersonFieldDefaultSettings = z.infer<typeof personFieldDefaultSettingsSchema>;

export const platformDefaultSchema = z
  .object({
    id: z.uuid(),
    kind: z.enum(PLATFORM_DEFAULT_KINDS),
    /** Nøkkelen. Sammenlignes uten skrivemåte (`app.platform_default_key`): «Cerfew» = «Curfew». */
    key: z.string(),
    /** Scenetidens navn og personfeltets etikett. Null for en ressursmal: navnet står i språkfila. */
    name: z.string().nullable(),
    sortOrder: z.number().int(),
    /** On stage og Off stage: kan ikke slettes, døpes om eller slås av. */
    locked: z.boolean(),
    /** Med i lista nye festivaler og organisasjoner får. */
    active: z.boolean(),
    /** Scenetid: `StageTimeDefaultSettings`. Personfelt: `PersonFieldDefaultSettings`. Mal: svarene. */
    settings: z.record(z.string(), z.unknown()),
    updatedAt: z.iso.datetime({ offset: true }),
  })
  .meta({ id: 'PlatformDefault' });
export type PlatformDefault = z.infer<typeof platformDefaultSchema>;

export const platformDefaultListResponseSchema = z
  .object({ items: z.array(platformDefaultSchema) })
  .meta({ id: 'PlatformDefaultListResponse' });
export type PlatformDefaultListResponse = z.infer<typeof platformDefaultListResponseSchema>;

/**
 * Det Control Room kan endre på én rad. Nøkkelen og bindingen står fast; en låst scenetid får bare ny
 * plass, varighet og strek (basen sier nei til resten, 22023).
 */
export const updatePlatformDefaultRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(60).optional(),
    sortOrder: z.number().int().min(0).max(100_000).optional(),
    active: z.boolean().optional(),
    settings: z
      .object({
        occupiesStage: z.boolean().optional(),
        defaultMinutes: z.number().int().min(1).max(1440).nullable().optional(),
        stageLine: z.boolean().optional(),
        shownIn: z.array(z.enum(PERSON_FIELD_SURFACES)).optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .refine((body) => Object.keys(body).length > 0, { message: 'Nothing to change.' })
  .meta({ id: 'UpdatePlatformDefaultRequest' });
export type UpdatePlatformDefaultRequest = z.infer<typeof updatePlatformDefaultRequestSchema>;

export const createPlatformStageTimeRequestSchema = z
  .object({ name: z.string().trim().min(1).max(60) })
  .strict()
  .meta({ id: 'CreatePlatformStageTimeRequest' });
export type CreatePlatformStageTimeRequest = z.infer<typeof createPlatformStageTimeRequestSchema>;

/**
 * En mal slik typebyggeren bruker den: svarene, validert som en ny type. `festivalId` er alltid null
 * her — nivået velges når typen lages (`.omit` går ikke på et skjema med `superRefine` i zod 4).
 * Navnet i svaret er reserven; skjermen viser `resources.preset.<key>`.
 */
export const resourceTypePresetSchema = z
  .object({ key: z.string(), body: createResourceTypeRequestSchema })
  .meta({ id: 'ResourceTypePresetDto' });
export type ResourceTypePresetDto = z.infer<typeof resourceTypePresetSchema>;

export const resourceTypePresetListResponseSchema = z
  .object({ items: z.array(resourceTypePresetSchema) })
  .meta({ id: 'ResourceTypePresetListResponse' });
export type ResourceTypePresetListResponse = z.infer<typeof resourceTypePresetListResponseSchema>;
